import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, of, tap } from 'rxjs';
import { EndpointList } from '../endpoint-list';
import { jwtDecode } from 'jwt-decode';
import { Roles, RoleType } from '../constants/roles.constants';

interface DecodedToken {
  email: string;
  firstName: string;
  lastName: string;
  role: RoleType;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isAuthenticated: WritableSignal<boolean> = signal(false);

  constructor(
    private http: HttpClient,
    private router: Router,
    private jwtHelper: JwtHelperService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.checkInitialAuthState();
  }

  private checkInitialAuthState() {
    this.isAuthenticated.set(this.isUserAuthenticated());
  }

  login(form: any): Observable<any> {
    return this.http.post<any>(EndpointList.login(), form).pipe(
      tap(response => {
        localStorage.setItem('token', response.access_token);
        this.isAuthenticated.set(true);
      }),
    );
  }

  logout(): void {
    this.http.post<any>(EndpointList.logout(), {}).subscribe();

    localStorage.removeItem('token');
    this.isAuthenticated.set(false);

    this.router.navigate(['/', 'auth', 'login']).then(() => {
      window.location.reload();
    });
  }

  isUserAuthenticated(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const token = localStorage.getItem('token');

    if (!token) {
      return false;
    }

    try {
      return !this.jwtHelper.isTokenExpired(token);
    } catch (e) {
      localStorage.removeItem('token');
      return false;
    }
  }

  checkAuthStatus(): Observable<boolean> {
    return of(this.isUserAuthenticated());
  }

  forgotPassword(form: any): Observable<any> {
    return this.http.post<any>(EndpointList.forgotPassword(), form);
  }

  saveNewPassword(params: any): Observable<any> {
    return this.http.post<any>(EndpointList.resetPasswordConfirm(), params);
  }

  verifyToken(token: string): Observable<any> {
    return this.http.get<any>(EndpointList.resetPassword(token));
  }

  // registration(form: any): Observable<any> {
  //   return this.http.post<any>(EndpointList.registration(), form);
  // }

  hasRole(roleToCheck: RoleType): boolean {
    const userRole = this.getUserRole();
    // Admin has access to everything
    if (userRole === Roles.ADMIN) return true;
    // Manager has access to manager and student stuff
    if (userRole === Roles.MANAGER && roleToCheck !== Roles.ADMIN) return true;
    // Student only has access to student stuff
    return userRole === roleToCheck;
  }

  isAdmin(): boolean {
    return this.getUserRole() === Roles.ADMIN;
  }

  isManager(): boolean {
    return this.getUserRole() === Roles.MANAGER;
  }

  private getUserRole(): RoleType | undefined {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return Roles.MANAGER; // Default to lowest privilege

    try {
      const decodedToken: DecodedToken = jwtDecode(token);
      return decodedToken.role || Roles.MANAGER;
    } catch (error) {
      return Roles.MANAGER;
    }
  }
}